import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  InformationCircleIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { UserInfo } from 'pages/ManageAccounts'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { downloadBrokerAgreementPdf, getAdminConfig, setAdminConfig } from 'services'
import { Button, ButtonGroup } from 'stories/components'
import { capitalizeFirstLetter, confirm, generateErrorHtml } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import { validatePlaceholders } from '../LoanTermSheet/logic'
import { BrokerAgreementPlaceholders, brokerOptions } from './constants'

export const BrokerAgreement = ({
  user,
  onUpdate,
  onClose,
}: {
  user?: UserInfo
  onUpdate?: Function
  onClose?: Function
}) => {
  const [action, setAction] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [changed, setChanged] = useState(false)
  const [data, setData] = useState<Record<string, any>>([])
  const [template, setTemplate] = useState(user?.accountType || 'broker')
  const [errors, setErrors] = useState<Record<number, any>>({})

  const isUserAgreement = !!user?.id

  const initData = async () => {
    setIsLoading(true)
    if (isUserAgreement && user.agreementTemp?.length) {
      setData(user.agreementTemp)
    } else {
      const res = await getAdminConfig('brokerAgreementTemplate', { template })
      setData(res || [])
    }
    setIsLoading(false)
    setChanged(false)
    setAction('')
  }
  useEffect(() => {
    initData()
  }, [template])

  const onChange = (key: string, value: any) => {
    const params = key.split('.')
    const temp = cloneDeep(data)
    temp[params[0]][params[1]] = value
    setData(temp)
    setChanged(true)
  }

  const onChangeTemplate = async (value: string) => {
    if (changed) await onSubmit('changeTemplate')
    setTemplate(value)
  }

  const onSubmit = async (action?: string) => {
    const placeholders = BrokerAgreementPlaceholders.map((v) => capitalizeFirstLetter(v))
    const temp: Record<number, any> = {}
    let hasError = false
    let _errorPlaceholders: string[] = []
    data.map((item: any, index: number) => {
      ;['title', 'content'].map((key) => {
        const { hasError: error, errorPlaceholders } = validatePlaceholders(item[key], placeholders, true, false)
        if (error) {
          if (!temp[index]) temp[index] = []
          temp[index].push(key)
          hasError = true
          _errorPlaceholders.push(...errorPlaceholders)
        }
      })
    })
    setErrors(temp)
    if (hasError) {
      const html = generateErrorHtml(
        'Following Placeholders are not valid. Please check them again.',
        _errorPlaceholders.map((v) => `{{${v}}}`),
      )
      await confirm(html, { titleYes: false, titleNo: 'Close' })
      return
    }
    if (isUserAgreement) {
      onUpdate && onUpdate(data)
      return
    }
    if (action === 'changeTemplate') {
      const result = await confirm('Do you want to save the changes before switching to the new template?')
      if (!result) return
    }
    setIsLoading(true)
    await setAdminConfig('brokerAgreementTemplate', { data, template })
    toast('Successfully submitted', { type: 'success' })
    setIsLoading(false)
    setChanged(false)
  }

  const onAddClick = () => {
    const temp = cloneDeep(data)
    temp.push({ title: 'New Title', content: 'Please enter content here...', isSign: false, isNewPage: false })
    setData(temp)
  }

  const onDelete = async (index: number) => {
    const result = await confirm('Are you sure you want to delete this section?')
    if (!result) return
    const temp = cloneDeep(data)
    temp.splice(index, 1)
    setData(temp)
  }
  const onDownloadBrokerAgreementPdf = () => {
    downloadBrokerAgreementPdf(user?.id || 0, template)
  }

  const updateLatest = async () => {
    const isContinue = await confirm('Are you sure you want to update from Master Template?')
    if (!isContinue) return
    onUpdate && onUpdate([])
  }

  return (
    <div className={`relative ${isUserAgreement ? 'border rounded py-2 px-3 my-2' : ''}`}>
      <LayoutLoading show={isLoading} />
      <h2 className={`text-2xl font-bold flex items-center mb-4 ${isUserAgreement ? 'mt-2' : ''}`}>
        Third Party Agreement Template
      </h2>
      {!isUserAgreement && (
        <div className="mb-6">
          <p className="ml-3 text-lg font-medium mb-2">- Placeholders</p>
          <div className="p-3 border rounded-md">
            <div className="flex gap-x-1 gap-y-3 flex-wrap justify-between">
              {BrokerAgreementPlaceholders.sort().map((item, index) => {
                return (
                  <pre key={index} className="w-[240px]">
                    {capitalizeFirstLetter(item)}
                  </pre>
                )
              })}
            </div>
          </div>
        </div>
      )}
      <div className={`flex items-center justify-${isUserAgreement ? 'end' : 'between'}`}>
        {!isUserAgreement && (
          <ButtonGroup
            title={brokerOptions()}
            value={template}
            onChange={(v) => onChangeTemplate(v)}
            className="mb-4"
          />
        )}
        {isUserAgreement && user.agreementTemp?.length ? (
          <div className="flex items-center gap-2 text-shade-blue italic flex-1">
            <InformationCircleIcon className="w-4 h-4" />
            <span>This agreement has been modified from the original template.</span>
            <Button outline size="12px" onClick={updateLatest}>
              <div className="flex gap-2 items-center">
                <ArrowPathIcon className="w-4 h-4" />
                Update From Master Template
              </div>
            </Button>
          </div>
        ) : (
          <></>
        )}
        <Button link onClick={onDownloadBrokerAgreementPdf} loading={action === 'download'}>
          <div className="flex gap-2 items-center">
            Download template
            <ArrowDownTrayIcon className="w-4 h-4" />
          </div>
        </Button>
        {isUserAgreement && onClose && (
          <span
            className="text-[14px] flex items-center ml-2 gap-1 text-red-800 hover:underline cursor-pointer"
            onClick={() => onClose()}
          >
            Close
            <XMarkIcon className="w-4 h-4"></XMarkIcon>
          </span>
        )}
      </div>
      {data?.map((item: any, index: number) => {
        return (
          <div className="mb-6 relative">
            <div className="grid md:grid-cols-2 grid-cols-1">
              <RenderInput
                input={{
                  inputType: 'text',
                  title: 'Title',
                  value: item.title || '',
                  error: errors[index]?.includes('title') ? 'Invalid placeholders exist' : '',
                }}
                Key={`${index}.title`}
                onChange={onChange}
              />
              <div className="flex justify-end items-center gap-4 ">
                <RenderInput
                  input={{
                    inputType: 'toggle',
                    title: 'Signature',
                    value: item.isSign || false,
                  }}
                  Key={`${index}.isSign`}
                  onChange={onChange}
                />
                <RenderInput
                  input={{
                    inputType: 'toggle',
                    title: 'New Page',
                    value: item.isNewPage || false,
                  }}
                  Key={`${index}.isNewPage`}
                  onChange={onChange}
                />
                <Button link onClick={() => onDelete(index)}>
                  <div className="flex items-center gap-1 text-red-600">
                    <TrashIcon className="w-4 h-4"></TrashIcon>
                    Delete
                  </div>
                </Button>
              </div>
            </div>

            <RenderInput
              input={{
                inputType: 'textarea',
                title: 'Text',
                value: item.content,
                error: errors[index]?.includes('content') ? 'Invalid placeholders exist' : '',
                rows: 15,
              }}
              Key={`${index}.content`}
              onChange={onChange}
            />
          </div>
        )
      })}
      <Button link onClick={onAddClick}>
        <div className="flex gap-2 items-center">
          <PlusIcon className="w-4 h-4"></PlusIcon> Add Section
        </div>
      </Button>
      <div className="flex justify-center mt-4">
        <Button onClick={onSubmit} loading={isLoading}>
          Save
        </Button>
      </div>
    </div>
  )
}
